/**
 * /phone/:code — the side camera.
 *
 * Built for a mid-range Android held at arm's length, propped against a book:
 * big touch targets (44px minimum), high contrast, one instruction at a time,
 * no small print, and no layout that needs two hands.
 *
 * The phone is a renderer. The laptop publishes a stage; this page shows the
 * matching screen. It never decides what step the student is on.
 *
 * Recording is continuous from the moment it connects and is deliberately NOT
 * torn down between stages — a stage change must never interrupt the camera or
 * drop the queued chunks.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  Camera,
  Check,
  RefreshCw,
  Video,
  WifiOff,
} from "lucide-react";
import type { AttemptMediaKind } from "@exam/shared";
import { ATTEMPT_STAGE_LABEL } from "@exam/shared";
import { cn } from "@/lib/cn";
import { sendProctorEvents, uploadPhoto } from "@/lib/exam-api";
import { usePhoneSession } from "@/lib/phone/usePhoneSession";
import { useViewfinderPublish } from "@/lib/phone/useViewfinderPublish";
import { useWakeLock } from "@/lib/phone/useWakeLock";
import type {
  CameraDiagnostics,
  CameraState,
  RecordingUiState,
} from "@/lib/recorder";
import {
  BITRATE_LADDER,
  beepError,
  captureStill,
  formatMB,
  unlockBeeps,
  useCamera,
  useRecorder,
  useRecordingBeeps,
  useRecordingUi,
  useUploadHealth,
  type UploadHealth,
} from "@/lib/recorder";
import { formatDuration } from "@/lib/exam/serverClock";
import { useVersionWatch } from "@/lib/version";
import { PairCodeEntry } from "./components/PairCodeEntry";
import { PhoneButton } from "./components/PhoneButton";
import { AnswerSheetStep } from "./components/AnswerSheetStep";

export function PhonePage() {
  const { code = "" } = useParams();
  if (!code) return <PairCodeEntry />;
  return <PhoneSurface pairCode={code} />;
}

function PhoneSurface({ pairCode }: { pairCode: string }) {
  const session = usePhoneSession(pairCode);
  // `autoRequest: false` — the rear camera opens only from the student's tap on
  // the "Enable camera" button, never from an ungestured call on mount. On
  // Android Chrome an ungestured call can be silently blocked and persist that
  // decision for the site, which is the failure this page was hitting.
  const camera = useCamera({
    facingMode: "environment",
    audio: true,
    autoRequest: false,
  });
  const wakeLock = useWakeLock(session.state === "connected");

  const connected = session.state === "connected";
  const recordingWanted =
    connected &&
    session.attemptId !== null &&
    session.stage !== "done" &&
    session.attemptStatus !== "submitted" &&
    session.attemptStatus !== "auto_submitted";

  /**
   * Tells the server the side camera stopped recording.
   *
   * Sent directly rather than through a `ProctorQueue`: the phone has no
   * queue of its own, this fires at most a handful of times in an exam, and
   * the event is the whole point — a failure to send it must not be retried
   * into silence. `PHONE_DISCONNECTED` is the closest existing type; the
   * metadata says which kind of stop it was.
   */
  const reportStall = useCallback(
    async (info: {
      silentForMs: number;
      willRestart: boolean;
      reason: "encoder" | "storage";
    }) => {
      const attemptId = session.attemptId;
      const token = session.deviceToken;
      if (!attemptId || !token) return;
      try {
        await sendProctorEvents(
          attemptId,
          [
            {
              type: "PHONE_DISCONNECTED",
              ts_ms: Math.round(session.clock.now()),
              severity: "critical",
              metadata: {
                reason:
                  info.reason === "storage"
                    ? "phone could not save video locally"
                    : "phone recorder produced no video",
                silent_for_ms: info.silentForMs,
                recovering: info.willRestart,
                detected_by: "phone",
              },
            },
          ],
          token,
        );
      } catch {
        // The recording is already in trouble; a failed report must not also
        // take down the page that is trying to recover it.
      }
    },
    [session.attemptId, session.deviceToken, session.clock],
  );

  const recorder = useRecorder({
    attemptId: session.attemptId,
    source: "phone",
    stream: camera.stream,
    clock: session.clock,
    deviceToken: session.deviceToken ?? undefined,
    // Low-data mode: 250 kbps at 640x480/15fps. This is a room/side view — the
    // student must be recognizable, not high-fidelity — and it runs on mobile data.
    videoBitsPerSecond: BITRATE_LADDER.phone[0],
    recording: recordingWanted,
    // The stall watchdog matters MORE here than on the laptop. The phone is
    // placed aside, face down to the room, with nobody looking at it — so a
    // MediaRecorder that goes quiet while its track stays live is invisible
    // unless something says so. Without this the phone kept the exact failure
    // the laptop was just fixed for.
    onStall: ({ silentForMs, willRestart, reason }) =>
      void reportStall({ silentForMs, willRestart, reason }),
  });

  // One honest recording state, and — since the phone is placed aside and its
  // screen is not watched — an audible beep on every change of it.
  const recUi = useRecordingUi({
    wanted: recordingWanted,
    cameraState: camera.state,
    recording: recorder.status.recording,
    stalled: recorder.status.stalled,
  });
  useRecordingBeeps(recUi.state);

  // Is the phone's footage actually REACHING the server? On mobile data a
  // silent upload stall is the exact failure this must surface — and a
  // recorder producing nothing has an EMPTY queue, which the queue's own
  // numbers cannot distinguish from healthy.
  const uploadHealth = useUploadHealth(
    recorder.status.upload,
    recorder.status.stalled,
  );

  // The phone is placed aside — beep once when footage stops reaching the
  // server, so the student hears it rather than having to glance.
  const prevUploadStateRef = useRef<UploadHealth["state"]>(uploadHealth.state);
  useEffect(() => {
    const prev = prevUploadStateRef.current;
    prevUploadStateRef.current = uploadHealth.state;
    const bad = (s: UploadHealth["state"]) =>
      s === "stalled" || s === "not-recording";
    if (!bad(prev) && bad(uploadHealth.state)) beepError();
  }, [uploadHealth.state]);

  const version = useVersionWatch();

  // Unlock audio on the first tap so the beeps are allowed to play on mobile.
  useEffect(() => {
    const onFirstTap = () => unlockBeeps();
    window.addEventListener("pointerdown", onFirstTap, { once: true });
    return () => window.removeEventListener("pointerdown", onFirstTap);
  }, []);

  /* -- warn loudly if the student backgrounds the page ---------------- */

  const [wasHidden, setWasHidden] = useState(false);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") setWasHidden(true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  /* -- photo capture, shared by every capture step -------------------- */

  const [uploading, setUploading] = useState(false);
  const videoElRef = camera.videoRef;

  // The laptop shows this while the student is aiming the phone, and only
  // then. `idle` is included because the phone learns the stage from its
  // heartbeat up to 15 seconds late: gating on `placement` alone would leave
  // the laptop showing "waiting for the phone" for fifteen seconds in the
  // ordinary case, and a student who thinks something is wrong picks the
  // phone up — losing the position this exists to protect.
  //
  // The server enforces the same window, so a phone that has not yet heard
  // that placement was confirmed cannot recreate the frame after the laptop
  // deletes it.
  useViewfinderPublish({
    attemptId: session.attemptId,
    deviceToken: session.deviceToken,
    videoRef: videoElRef,
    enabled:
      camera.state === "ready" &&
      (session.stage === "idle" || session.stage === "placement"),
  });

  const capture = useCallback(async (): Promise<Blob | null> => {
    const el = videoElRef.current;
    if (!el) return null;
    return captureStill(el, 0.85);
  }, [videoElRef]);

  const upload = useCallback(
    async (blob: Blob, kind: AttemptMediaKind): Promise<boolean> => {
      if (!session.attemptId || !session.deviceToken) return false;
      setUploading(true);
      try {
        await uploadPhoto({
          attemptId: session.attemptId,
          kind,
          blob,
          deviceToken: session.deviceToken,
        });
        return true;
      } catch {
        return false;
      } finally {
        setUploading(false);
      }
    },
    [session.attemptId, session.deviceToken],
  );

  /* -- render ---------------------------------------------------------- */

  if (session.state === "failed") {
    return (
      <PhoneShell>
        <div className="flex flex-col items-center gap-4 px-5 py-10 text-center">
          <AlertTriangle className="size-8 text-violation" />
          <h1 className="text-lg font-semibold text-ink">Could not connect</h1>
          <p className="text-sm text-ink-muted">{session.errorMessage}</p>
          <PhoneButton onClick={session.retry} icon={<RefreshCw />}>
            Try again
          </PhoneButton>
        </div>
      </PhoneShell>
    );
  }

  return (
    <PhoneShell>
      <StatusBar
        connected={connected}
        reconnecting={session.state === "reconnecting"}
        recordingState={recUi.state}
        elapsedMs={recUi.elapsedMs}
        upload={uploadHealth}
        stageLabel={ATTEMPT_STAGE_LABEL[session.stage]}
      />

      {recUi.state === "stopped" ? (
        <RecordingStoppedWarning onRetry={() => void camera.request()} />
      ) : recUi.state === "reconnecting" ? (
        <RecordingReconnecting />
      ) : null}

      {uploadHealth.state === "stalled" ? (
        <UploadStalledWarning upload={uploadHealth} />
      ) : null}

      {wasHidden ? (
        <BackgroundWarning onDismiss={() => setWasHidden(false)} />
      ) : null}

      {!wakeLock.active && wakeLock.supported === false ? (
        <Notice>
          Set your screen timeout to “never” in your phone settings — if the
          screen sleeps, the camera stops.
        </Notice>
      ) : null}

      {version.updateAvailable ? (
        <div className="border-b border-accent/30 bg-accent-soft px-4 py-2.5">
          <p className="text-xs text-ink">A newer version is available.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-1 min-h-11 text-xs font-medium text-accent underline"
          >
            Reload to update
          </button>
        </div>
      ) : null}

      <CameraSurface
        videoRef={camera.videoRef}
        state={camera.state}
        errorMessage={camera.errorMessage}
        diagnostics={camera.diagnostics}
        onRetry={() => void camera.request()}
      />

      <div className="flex-1 px-4 py-4">
        {camera.state !== "ready" ? null : session.stage === "submitted_upload" ? (
          <AnswerSheetStep
            capture={capture}
            upload={(blob) => upload(blob, "answer_sheet")}
            uploading={uploading}
            uploadedCount={session.mediaCounts.answer_sheet ?? 0}
          />
        ) : session.stage === "placement" ? (
          <StageMessage
            title="Position your phone"
            lines={[
              "Stand the phone to one side and slightly behind you.",
              "Check the picture above shows your desk, both hands, the edge of your laptop screen, and you.",
              "Then confirm on the laptop.",
            ]}
          />
        ) : session.stage === "recording" ? (
          <StageMessage
            title="Exam in progress"
            lines={[
              "Leave the phone exactly where it is.",
              "Do not pick it up, lock it, or switch apps.",
              "This screen must stay open until you have submitted.",
            ]}
          />
        ) : session.stage === "done" ? (
          <StageMessage
            title="All done"
            lines={["You can put the phone away now."]}
          />
        ) : (
          <StageMessage
            title="Connected"
            lines={["Follow the steps on your laptop."]}
          />
        )}
      </div>
    </PhoneShell>
  );
}

/* ------------------------------------------------------------------ */
/* Chrome                                                              */
/* ------------------------------------------------------------------ */

function PhoneShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-app">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col">
        {children}
      </div>
    </div>
  );
}

function StatusBar({
  connected,
  reconnecting,
  recordingState,
  elapsedMs,
  upload,
  stageLabel,
}: {
  connected: boolean;
  reconnecting: boolean;
  recordingState: RecordingUiState;
  elapsedMs: number | null;
  upload: UploadHealth;
  stageLabel: string;
}) {
  const rec = recordingState === "recording";
  const label =
    recordingState === "recording"
      ? "Recording"
      : recordingState === "reconnecting"
        ? "Reconnecting"
        : recordingState === "stopped"
          ? "Stopped"
          : "Paused";
  const tone =
    recordingState === "recording" || recordingState === "stopped"
      ? "violation"
      : recordingState === "reconnecting"
        ? "review"
        : "muted";

  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
      <span
        className={cn(
          "flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5",
          tone === "violation"
            ? "border-violation/30 bg-violation-soft"
            : tone === "review"
              ? "border-review/30 bg-review-soft"
              : "border-border bg-surface-sunken",
        )}
      >
        <Video
          className={cn(
            "size-3.5",
            tone === "violation"
              ? "text-violation"
              : tone === "review"
                ? "text-review"
                : "text-ink-faint",
          )}
        />
        <span
          className={cn(
            "text-2xs font-medium",
            tone === "violation"
              ? "text-violation"
              : tone === "review"
                ? "text-review"
                : "text-ink-faint",
          )}
        >
          {label}
        </span>
        {rec && elapsedMs !== null ? (
          <span className="tabular text-2xs font-medium text-violation">
            {formatDuration(elapsedMs)}
          </span>
        ) : null}
      </span>

      <span className="truncate text-2xs text-ink-muted">{stageLabel}</span>

      <span className="ml-auto flex items-center gap-1.5">
        {upload.pending > 0 ? (
          <span
            className={cn(
              "tabular text-2xs",
              upload.state === "stalled"
                ? "font-semibold text-violation"
                : upload.state === "behind"
                  ? "text-review"
                  : "text-ink-faint",
            )}
          >
            {upload.pending}↑
          </span>
        ) : upload.uploaded > 0 ? (
          <span className="tabular text-2xs text-ink-faint">
            {upload.uploaded} sent
          </span>
        ) : null}
        {reconnecting ? (
          <WifiOff className="size-3.5 text-review" />
        ) : connected ? (
          <Check className="size-3.5 text-answered" />
        ) : (
          <WifiOff className="size-3.5 text-ink-faint" />
        )}
      </span>
    </div>
  );
}

function UploadStalledWarning({ upload }: { upload: UploadHealth }) {
  return (
    <div className="border-b border-violation/30 bg-violation-soft px-4 py-3">
      <p className="flex items-start gap-2 text-sm font-semibold text-violation">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        Recording is not reaching the server
      </p>
      <p className="mt-1 text-xs text-ink">
        Your connection looks too weak to upload right now.{" "}
        <span className="tabular">{upload.pending}</span> segment
        {upload.pending === 1 ? "" : "s"}
        {upload.pendingBytes > 0 ? ` (${formatMB(upload.pendingBytes)})` : ""}{" "}
        are waiting. Keep this page open and move somewhere with better signal —
        they will upload automatically when the connection recovers.
      </p>
    </div>
  );
}

function RecordingStoppedWarning({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="border-b border-violation/30 bg-violation-soft px-4 py-3">
      <p className="flex items-start gap-2 text-sm font-semibold text-violation">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        Recording has stopped
      </p>
      <p className="mt-1 text-xs text-ink">
        The side camera is not recording. Tap below to turn it back on, and check
        nothing is covering the lens.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 min-h-11 text-xs font-medium text-violation underline"
      >
        Turn the camera back on
      </button>
    </div>
  );
}

function RecordingReconnecting() {
  return (
    <div className="border-b border-review/30 bg-review-soft px-4 py-2.5">
      <p className="flex items-center gap-2 text-xs font-medium text-review">
        <RefreshCw className="size-3.5" />
        Reconnecting the camera…
      </p>
    </div>
  );
}

function CameraSurface({
  videoRef,
  state,
  errorMessage,
  diagnostics,
  onRetry,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  state: CameraState;
  errorMessage: string | null;
  diagnostics: CameraDiagnostics;
  onRetry: () => void;
}) {
  // "denied" is a permission problem the student can fix; "error"/"unavailable"
  // means the browser reached the camera but it would not start (busy, or no
  // device). Saying which one it is stops the student re-tapping "allow" on a
  // problem that allowing cannot fix.
  const heading =
    state === "denied"
      ? "Camera access is blocked"
      : state === "unavailable"
        ? "No camera available"
        : state === "error"
          ? "The camera could not start"
          : "Turn on the camera";

  const buttonLabel =
    state === "requesting"
      ? "Starting…"
      : state === "idle" || state === "denied"
        ? "Enable camera"
        : "Try again";

  return (
    <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden border-b border-border bg-ink">
      <video
        ref={videoRef}
        muted
        playsInline
        className="size-full object-cover"
      />

      {state !== "ready" ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 overflow-y-auto bg-surface px-5 py-4 text-center">
          <Camera className="size-8 text-ink-faint" />
          <p className="text-sm font-medium text-ink">{heading}</p>
          <p className="text-xs text-ink-muted">
            {errorMessage ?? "Tap the button below to turn on the camera."}
          </p>
          {/* Must be a real tap: iOS Safari only honours getUserMedia from a
              user gesture, and auto-calling on mount is silently ignored. */}
          <PhoneButton onClick={onRetry} disabled={state === "requesting"}>
            {buttonLabel}
          </PhoneButton>
          <CameraDiagnosticsLine state={state} diagnostics={diagnostics} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Lightweight on-screen diagnostics so a bug report tells us exactly what
 * failed — the getUserMedia error name, whether the page is a secure context,
 * and whether the browser exposes a camera API at all.
 */
function CameraDiagnosticsLine({
  state,
  diagnostics,
}: {
  state: CameraState;
  diagnostics: CameraDiagnostics;
}) {
  return (
    <details className="mt-1 w-full max-w-xs text-left">
      <summary className="cursor-pointer text-2xs text-ink-faint">
        Camera not working? Show details
      </summary>
      <dl className="mt-2 rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-ink-muted">
        <div className="flex justify-between gap-3">
          <dt>state</dt>
          <dd className="text-ink">{state}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>error</dt>
          <dd className="text-ink">{diagnostics.errorName ?? "none"}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>secureContext</dt>
          <dd className="text-ink">{String(diagnostics.secureContext)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>mediaDevices</dt>
          <dd className="text-ink">{String(diagnostics.hasMediaDevices)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>attempts</dt>
          <dd className="text-ink">{diagnostics.attempts}</dd>
        </div>
        {diagnostics.errorMessage ? (
          <p className="mt-1 break-words text-ink-faint">
            {diagnostics.errorMessage}
          </p>
        ) : null}
      </dl>
    </details>
  );
}

function BackgroundWarning({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="border-b border-violation/30 bg-violation-soft px-4 py-3">
      <p className="flex items-start gap-2 text-sm font-medium text-violation">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        Do not leave this page
      </p>
      <p className="mt-1 text-xs text-ink">
        Switching apps or locking the phone stops the camera. Keep this screen
        open for the whole exam.
      </p>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-2 min-h-11 text-xs font-medium text-violation underline"
      >
        I understand
      </button>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-review/30 bg-review-soft px-4 py-2.5">
      <p className="text-xs text-ink">{children}</p>
    </div>
  );
}

function StageMessage({
  title,
  lines,
}: {
  title: string;
  lines: string[];
}) {
  return (
    <div>
      <h1 className="text-lg font-semibold text-ink">{title}</h1>
      <ul className="mt-3 flex flex-col gap-2">
        {lines.map((line) => (
          <li key={line} className="text-sm text-ink-muted">
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}
